import { setRetryMessage, RETRY_MESSAGE } from 'utils/retryMessageStore';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

function is5xxOrNetwork(error) {
  const status = error.response?.status;
  if (status != null) return status >= 500;
  return error.code === 'ECONNABORTED' || error.message === 'Network Error';
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Lógica de retry com mensagem no loading. Reutilizável para login e outras APIs.
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {{ shouldRetry: (config: import('axios').AxiosRequestConfig) => boolean }} options.shouldRetry - se true, aplica retry e exibe mensagem
 */
function retryRequest(axiosInstance, failedRequest, shouldRetry) {
  if (!shouldRetry(failedRequest.config)) {
    return Promise.reject(failedRequest);
  }

  const config = { ...failedRequest.config, retryCount: (failedRequest.config.retryCount || 0) + 1 };

  if (config.retryCount > MAX_RETRIES) {
    setRetryMessage(null);
    return Promise.reject(failedRequest);
  }

  setRetryMessage(RETRY_MESSAGE);
  const delayMs = BASE_DELAY_MS * Math.pow(2, config.retryCount - 1);

  return delay(delayMs)
    .then(() => axiosInstance.request(config))
    .then(response => {
      setRetryMessage(null);
      return response;
    });
}

/**
 * Cria um interceptor de resposta que aplica retry + mensagem em 5xx/rede
 * apenas quando shouldRetry(config) for true.
 *
 * Uso em outras APIs:
 * 1) Por URL – em config.js da instância:
 *    const shouldRetry = (config) => config.url === '/v1/auth' || config.url?.includes('/v1/empresas');
 *    axiosInstance.interceptors.response.use(res => res, createRetryOn5xxInterceptor(axiosInstance, shouldRetry, handleAuthError));
 *
 * 2) Por flag na requisição – no serviço, passar retryOn5xx: true no config;
 *    na página, ao usar loading global, chamar o serviço que já envia esse flag.
 *    shouldRetry = (config) => config.retryOn5xx === true;
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {(config: import('axios').AxiosRequestConfig) => boolean} shouldRetry
 * @param {(error: any) => Promise<never>} fallbackHandler - chamado quando não é retry ou após esgotar tentativas
 */
export function createRetryOn5xxInterceptor(axiosInstance, shouldRetry, fallbackHandler) {
  return function retryOn5xx(error) {
    if (is5xxOrNetwork(error) && shouldRetry(error.config)) {
      // Only wrap with fallbackHandler on the first attempt. Inner retries go through
      // this interceptor again (axios re-runs interceptors on each axiosInstance.request()),
      // so attaching .catch on every level would call fallbackHandler multiple times in
      // cascade — causing TypeErrors when it receives a response object instead of an error.
      if (!error.config?.retryCount) {
        return retryRequest(axiosInstance, error, shouldRetry).catch(fallbackHandler);
      }
      return retryRequest(axiosInstance, error, shouldRetry);
    }
    return fallbackHandler(error);
  };
}
